/**
 * Canonical defect-code enum.
 *
 * Every silent-recovery / partial-parse / out-of-spec site that the
 * parser, analysis, or derivation layers might emit is named here. The
 * code is the cross-layer key the UI's <DefectsTab> filters on, and the
 * one stable identifier external consumers (export bundles, fixtures,
 * tests) can match against.
 *
 * Codes are grouped by category prefix (RAW_, HEADER_, ADDRESS_, …) so
 * a UI that filters by category can do `code.startsWith("HEADER_")`.
 * The {@link defectCategory} helper returns the prefix (without the
 * trailing underscore) so callers don't have to reach for split.
 */

export const DEFECT = {
  // ---------- RAW_ — bytes / framing layer ----------
  RAW_NO_HEADER_SEPARATOR: "RAW_NO_HEADER_SEPARATOR",
  RAW_INVALID_LINE_ENDING: "RAW_INVALID_LINE_ENDING",
  RAW_TRUNCATED_INPUT: "RAW_TRUNCATED_INPUT",
  /**
   * RAW-002 / RFC5321-001. RFC 5322 §2.1.1 caps text lines (excluding CRLF)
   * at 998 octets; RFC 5321 §4.5.3.1.6 caps SMTP text lines at 1000 octets
   * including CRLF. Either limit being broken surfaces as this info-severity
   * defect; the parser still walks the full input.
   */
  RAW_LINE_LENGTH_EXCEEDED: "RAW_LINE_LENGTH_EXCEEDED",

  // ---------- HEADER_ — RFC 5322 header parsing ----------
  HEADER_INVALID_NAME: "HEADER_INVALID_NAME",
  HEADER_DUPLICATE_SINGLETON: "HEADER_DUPLICATE_SINGLETON",
  HEADER_FOLDING_MALFORMED: "HEADER_FOLDING_MALFORMED",
  HEADER_VALUE_NON_ASCII: "HEADER_VALUE_NON_ASCII",
  /**
   * An RFC 2047 encoded-word in a header value didn't match a supported
   * encoding (empty payload, mixed encodings within a single token, or a
   * language tag the decoder doesn't honour). Severity is `info` because the
   * parser still surfaces *something* (the raw payload) rather than dropping
   * the whole header.
   */
  HEADER_DECODE_FAILED: "HEADER_DECODE_FAILED",
  /**
   * An RFC 2231 parameter continuation / charset+language prefix could not be
   * decoded (malformed percent-encoding, missing segments, or an unknown
   * charset). The parameter falls back to its raw on-the-wire form so
   * consumers still see something.
   */
  HEADER_PARAM_DECODE_FAILED: "HEADER_PARAM_DECODE_FAILED",

  // ---------- ADDRESS_ — RFC 5322 address-list parsing ----------
  ADDRESS_MALFORMED: "ADDRESS_MALFORMED",
  ADDRESS_IDNA_DECODE_FAILED: "ADDRESS_IDNA_DECODE_FAILED",
  ADDRESS_GROUP_NESTED: "ADDRESS_GROUP_NESTED",
  /**
   * An SRS-rewritten Return-Path local-part (`SRS0=…=…@forwarder.example` /
   * `SRS1=…@forwarder.example`) could not be decoded because the segment
   * count, hash, or embedded original sender failed RFC-style parsing. The
   * address is kept as-is; downstream consumers see no recovered original.
   */
  ADDRESS_SRS_DECODE_FAILED: "ADDRESS_SRS_DECODE_FAILED",

  // ---------- DATE_ — RFC 5322 Date / Resent-Date parsing ----------
  DATE_INVALID: "DATE_INVALID",
  DATE_AMBIGUOUS_TIMEZONE: "DATE_AMBIGUOUS_TIMEZONE",

  // ---------- THREAD_ — Message-ID / In-Reply-To / References ----------
  THREAD_MISSING_MESSAGE_ID: "THREAD_MISSING_MESSAGE_ID",
  THREAD_REFERENCE_INVALID: "THREAD_REFERENCE_INVALID",

  // ---------- MIME_ — RFC 2045 / 2046 MIME tree ----------
  MIME_BOUNDARY_UNCLOSED: "MIME_BOUNDARY_UNCLOSED",
  MIME_BOUNDARY_MISSING: "MIME_BOUNDARY_MISSING",
  MIME_NESTED_TOO_DEEP: "MIME_NESTED_TOO_DEEP",
  MIME_PART_HEADER_MALFORMED: "MIME_PART_HEADER_MALFORMED",

  // ---------- ENCODING_ — Content-Transfer-Encoding decoders ----------
  ENCODING_BASE64_INVALID_PADDING: "ENCODING_BASE64_INVALID_PADDING",
  ENCODING_BASE64_INVALID_CHAR: "ENCODING_BASE64_INVALID_CHAR",
  ENCODING_QP_INVALID_ESCAPE: "ENCODING_QP_INVALID_ESCAPE",
  ENCODING_UNKNOWN: "ENCODING_UNKNOWN",

  // ---------- CHARSET_ — RFC 2047 charset decoders ----------
  CHARSET_UNKNOWN: "CHARSET_UNKNOWN",
  CHARSET_DECODE_FAILED: "CHARSET_DECODE_FAILED",

  // ---------- HTML_ — body-rendering risks ----------
  HTML_LINK_TEXT_MISMATCH: "HTML_LINK_TEXT_MISMATCH",
  HTML_INLINE_EVENT_HANDLER: "HTML_INLINE_EVENT_HANDLER",
  HTML_SANITIZER_REJECTED: "HTML_SANITIZER_REJECTED",

  // ---------- ATTACHMENT_ — file-disposition / safety ----------
  ATTACHMENT_MIME_MISMATCH: "ATTACHMENT_MIME_MISMATCH",
  ATTACHMENT_DOUBLE_EXTENSION: "ATTACHMENT_DOUBLE_EXTENSION",
  ATTACHMENT_FILENAME_UNSAFE: "ATTACHMENT_FILENAME_UNSAFE",

  // ---------- TRACE_ — Received chain ----------
  TRACE_RECEIVED_PARSE_FAILED: "TRACE_RECEIVED_PARSE_FAILED",
  TRACE_NEGATIVE_HOP_DELAY: "TRACE_NEGATIVE_HOP_DELAY",
  TRACE_CLOCK_SKEW: "TRACE_CLOCK_SKEW",

  // ---------- AUTH_ — DKIM / SPF / DMARC / ARC ----------
  AUTH_DKIM_SIGNATURE_MALFORMED: "AUTH_DKIM_SIGNATURE_MALFORMED",
  AUTH_DKIM_SELECTOR_MISSING: "AUTH_DKIM_SELECTOR_MISSING",
  /**
   * RFC 8301 §3.1 — DKIM-Signature carries `a=rsa-sha1`. SHA-1 is
   * deprecated for DKIM; verifiers SHOULD treat such signatures as
   * `policy` failures even if the body hash matches.
   */
  AUTH_DKIM_WEAK_ALGORITHM: "AUTH_DKIM_WEAK_ALGORITHM",
  /**
   * RFC 6376 §3.5 (`l=` tag) — the DKIM-Signature limits the signed body
   * to the first N octets. Any bytes BEYOND that count are not covered by
   * the signature, so an intermediary can append unsigned content (the
   * classic body-length / unsigned-append exploit) without invalidating
   * the cryptographic check. Emitted once per signature that carries `l=`.
   */
  AUTH_DKIM_PARTIAL_BODY: "AUTH_DKIM_PARTIAL_BODY",
  /**
   * RFC 6376 §5.4 — the From header MUST be included in the signed header
   * set (`h=`). When the signature names a non-empty `h=` list that omits
   * `from`, the originating address is unauthenticated even on an
   * otherwise-passing signature: an attacker can swap From freely. Emitted
   * once per signature whose `h=` list excludes `from`.
   */
  AUTH_DKIM_FROM_UNSIGNED: "AUTH_DKIM_FROM_UNSIGNED",
  AUTH_ARC_INSTANCE_GAP: "AUTH_ARC_INSTANCE_GAP",
  AUTH_ARC_CV_INVALID: "AUTH_ARC_CV_INVALID",
  AUTH_RESULTS_MALFORMED: "AUTH_RESULTS_MALFORMED",

  // ---------- REPORT_ — DSN / MDN / ARF reports ----------
  REPORT_DSN_INVALID_STATUS: "REPORT_DSN_INVALID_STATUS",
  REPORT_DSN_MISSING_FIELD: "REPORT_DSN_MISSING_FIELD",
  REPORT_MDN_MALFORMED: "REPORT_MDN_MALFORMED",
  /** MDN Disposition: header didn't match the RFC 8098 grammar. */
  REPORT_MDN_INVALID_DISPOSITION: "REPORT_MDN_INVALID_DISPOSITION",
  /** ARF Feedback-Type didn't match the RFC 5965 IANA registry. */
  REPORT_ARF_INVALID_FEEDBACK_TYPE: "REPORT_ARF_INVALID_FEEDBACK_TYPE",
  /** DMARC aggregate XML payload could not be parsed. */
  REPORT_DMARC_AGGREGATE_INVALID_XML: "REPORT_DMARC_AGGREGATE_INVALID_XML",
  /**
   * DMARC aggregate report arrived as a ZIP whose member could not be
   * extracted with the built-in single-file reader: the archive is
   * encrypted, spans multiple members, or uses an unsupported
   * compression method (anything other than stored / DEFLATE). The XML
   * cannot be recovered without a full zip library.
   */
  REPORT_DMARC_AGGREGATE_ZIP_UNSUPPORTED: "REPORT_DMARC_AGGREGATE_ZIP_UNSUPPORTED",
  /** TLSRPT JSON payload could not be parsed. */
  REPORT_TLSRPT_INVALID_JSON: "REPORT_TLSRPT_INVALID_JSON",

  // ---------- CRYPTO_ — S/MIME and PGP ----------
  CRYPTO_PROTECTED_HEADER_MISMATCH: "CRYPTO_PROTECTED_HEADER_MISMATCH",
  CRYPTO_SMIME_CERT_PARSE_FAILED: "CRYPTO_SMIME_CERT_PARSE_FAILED",
  CRYPTO_PGP_PACKET_INVALID: "CRYPTO_PGP_PACKET_INVALID",
  /**
   * An S/MIME encrypted part (`application/pkcs7-mime; smime-type=enveloped-data`
   * or a `.p7m`) carried non-trivial bytes but did NOT decode as a CMS
   * EnvelopedData blob (OID 1.2.840.113549.1.7.3) — the DER was truncated, the
   * smime-type label was wrong, or the layout is one the structural walker
   * can't follow. The part is still listed as `smime-enveloped`; only the
   * recipient/algorithm metadata is unavailable.
   */
  CRYPTO_SMIME_ENVELOPE_PARSE_FAILED: "CRYPTO_SMIME_ENVELOPE_PARSE_FAILED",

  // ---------- CALENDAR_ — iCalendar / vCard ----------
  CALENDAR_INVALID_RRULE: "CALENDAR_INVALID_RRULE",
  CALENDAR_MISSING_DTSTART: "CALENDAR_MISSING_DTSTART",

  // ---------- CONTACT_ — vCard variants (xCard XML, jCard JSON) ----------
  /** XCard (RFC 6351) XML payload could not be parsed. */
  CONTACT_INVALID_VCARD_XML: "CONTACT_INVALID_VCARD_XML",
  /** JCard (RFC 7095) JSON payload could not be parsed. */
  CONTACT_INVALID_VCARD_JSON: "CONTACT_INVALID_VCARD_JSON",

  // ---------- SECURITY_ — high-level safety verdicts ----------
  SECURITY_DANGEROUS_ATTACHMENT: "SECURITY_DANGEROUS_ATTACHMENT",
  SECURITY_LOOKALIKE_ADDRESS: "SECURITY_LOOKALIKE_ADDRESS",

  // ---------- LINK_ — URL inspection (W2 rewriters) ----------
  /**
   * A URL recognised as belonging to a known rewriter (Microsoft Safe Links,
   * Proofpoint URL Defense, Mimecast, Barracuda, Cisco, Google redirect,
   * generic ESP click-tracker) could not be decoded back to its underlying
   * target. The visible link is preserved; downstream consumers see the
   * rewritten URL with `original === rewritten`.
   */
  LINK_REWRITER_DECODE_FAILED: "LINK_REWRITER_DECODE_FAILED",

  // ---------- PROVIDER_ — vendor-specific X-headers ----------
  PROVIDER_HEADER_UNSUPPORTED: "PROVIDER_HEADER_UNSUPPORTED",
  PROVIDER_HEADER_MALFORMED: "PROVIDER_HEADER_MALFORMED",

  // ---------- REGISTRY_ — IANA registry lookups ----------
  REGISTRY_UNKNOWN_HEADER: "REGISTRY_UNKNOWN_HEADER",
  REGISTRY_DEPRECATED_HEADER: "REGISTRY_DEPRECATED_HEADER",

  // ---------- PROVENANCE_ — parse provenance / fingerprints ----------
  PROVENANCE_OUTPUT_HASH_MISMATCH: "PROVENANCE_OUTPUT_HASH_MISMATCH",
  PROVENANCE_INPUT_TRUNCATED: "PROVENANCE_INPUT_TRUNCATED",

  // ---------- LIMIT_ — Catalog H12 resource-budget trips ----------
  /**
   * Message source exceeded `ParserConfig.maxMessageBytes`. The source is cut
   * to the limit before any stage runs, so the parser walks a prefix and bytes
   * past the limit are not seen by any subsequent stage. Until 2026-08-26 this
   * paragraph described an intent the parser did not implement: it warned and
   * then scanned the whole source.
   */
  MESSAGE_BYTES_EXCEEDED: "MESSAGE_BYTES_EXCEEDED",
  /** Header block exceeded `ParserConfig.maxHeaderBytes`. */
  HEADER_BYTES_EXCEEDED: "HEADER_BYTES_EXCEEDED",
  /** Header field count exceeded `ParserConfig.maxHeaderCount`. */
  HEADER_COUNT_EXCEEDED: "HEADER_COUNT_EXCEEDED",
  /** MIME tree depth exceeded `ParserConfig.maxMimeDepth`. */
  MIME_DEPTH_EXCEEDED: "MIME_DEPTH_EXCEEDED",
  /** MIME part count exceeded `ParserConfig.maxMimePartCount`. */
  MIME_PART_COUNT_EXCEEDED: "MIME_PART_COUNT_EXCEEDED",
  /**
   * Embedded `message/rfc822` nesting exceeded
   * `ParserConfig.maxEmbeddedDepth`.
   */
  EMBEDDED_DEPTH_EXCEEDED: "EMBEDDED_DEPTH_EXCEEDED",
  /** Archive nesting depth exceeded `ParserConfig.maxArchiveDepth`. */
  ARCHIVE_DEPTH_EXCEEDED: "ARCHIVE_DEPTH_EXCEEDED",
  /** Decoded part bytes exceeded `ParserConfig.maxDecodedPartBytes`. */
  DECODED_PART_BYTES_EXCEEDED: "DECODED_PART_BYTES_EXCEEDED",
  /** Attachment bytes exceeded `ParserConfig.maxAttachmentBytes`. */
  ATTACHMENT_BYTES_EXCEEDED: "ATTACHMENT_BYTES_EXCEEDED",
} as const;

/** Union of every {@link DEFECT} value — the canonical defect-code type. */
export type DefectCode = (typeof DEFECT)[keyof typeof DEFECT];

/** Category prefix (without trailing underscore) of a defect code. */
export function defectCategory(code: string): string {
  const idx = code.indexOf("_");
  return idx < 0 ? code : code.slice(0, idx);
}
